/**
 * AdvancePaymentSettingsDialog - Configuration des emails d'acompte
 * Permet de configurer : destinataire, CC, et aperçu du template
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Mail, Plus, X, Loader2, Settings, Eye, Send,
  User, Clock, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdvancePaymentSettingsDialog({ open, onOpenChange, token }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hr_notification_email: "",
    cc_emails: [],
    sender_name: "BE-TAYE RH",
    reminder_enabled: true,
    send_time_j2: "08:30",
    send_time_j1: "08:30",
    send_time_j0: "08:30",
  });
  const [newCcEmail, setNewCcEmail] = useState("");
  
  // Preview state
  const [previewType, setPreviewType] = useState("j1");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/hr/advance-payments/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings({
        hr_notification_email: res.data.hr_notification_email || "",
        cc_emails: res.data.cc_emails || [],
        sender_name: res.data.sender_name || "BE-TAYE RH",
        reminder_enabled: res.data.reminder_enabled ?? true,
        send_time_j2: res.data.send_time_j2 || "08:30",
        send_time_j1: res.data.send_time_j1 || "08:30",
        send_time_j0: res.data.send_time_j0 || "08:30",
      });
    } catch (err) {
      console.error("Error fetching settings:", err);
      toast.error("Erreur lors du chargement des paramètres");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch preview
  const fetchPreview = useCallback(async (type) => {
    if (!token) return;
    
    setLoadingPreview(true);
    try {
      const res = await axios.get(
        `${API}/api/hr/advance-payments/email-preview?type=${type}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreview(res.data);
    } catch (err) {
      console.error("Error fetching preview:", err);
    } finally {
      setLoadingPreview(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) {
      fetchSettings();
      fetchPreview(previewType);
    }
  }, [open, fetchSettings, fetchPreview, previewType]);

  // Save settings
  const saveSettings = async () => {
    if (!settings.hr_notification_email) {
      toast.error("Veuillez saisir une adresse email principale");
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(settings.hr_notification_email)) {
      toast.error("L'adresse email principale n'est pas valide");
      return;
    }
    
    for (const cc of settings.cc_emails) {
      if (!emailRegex.test(cc)) {
        toast.error(`L'adresse CC "${cc}" n'est pas valide`);
        return;
      }
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/hr/advance-payments/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Paramètres enregistrés");
      onOpenChange(false);
    } catch (err) {
      console.error("Error saving settings:", err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  // Add CC email(s) - supports multiple emails separated by comma
  const addCcEmail = () => {
    const input = newCcEmail.trim();
    if (!input) return;
    
    // Split by comma and clean up
    const emails = input.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const validEmails = [];
    const invalidEmails = [];
    const duplicateEmails = [];
    
    for (const email of emails) {
      if (!emailRegex.test(email)) {
        invalidEmails.push(email);
      } else if (settings.cc_emails.includes(email) || validEmails.includes(email)) {
        duplicateEmails.push(email);
      } else {
        validEmails.push(email);
      }
    }
    
    // Show errors if any
    if (invalidEmails.length > 0) {
      toast.error(`Email(s) invalide(s): ${invalidEmails.join(', ')}`);
    }
    if (duplicateEmails.length > 0) {
      toast.warning(`Déjà ajouté(s): ${duplicateEmails.join(', ')}`);
    }
    
    // Add valid emails
    if (validEmails.length > 0) {
      setSettings(prev => ({
        ...prev,
        cc_emails: [...prev.cc_emails, ...validEmails]
      }));
      toast.success(`${validEmails.length} email(s) ajouté(s)`);
    }
    
    setNewCcEmail("");
  };

  // Remove CC email
  const removeCcEmail = (email) => {
    setSettings(prev => ({
      ...prev,
      cc_emails: prev.cc_emails.filter(e => e !== email)
    }));
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Paramètres des emails d'acompte
          </DialogTitle>
          <DialogDescription>
            Configurez les destinataires et visualisez le template des rappels
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="settings" className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Configuration
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Aperçu du template
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="flex-1 overflow-y-auto mt-4 pr-2">
            <div className="space-y-4">
              {/* Main recipient */}
              <div className="space-y-2">
                <Label htmlFor="hr_email" className="flex items-center gap-2">
                  <Send className="h-4 w-4 text-blue-600" />
                  Destinataire principal (À)
                </Label>
                <Input
                  id="hr_email"
                  type="email"
                  placeholder="rh@betaye.fr"
                  value={settings.hr_notification_email}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    hr_notification_email: e.target.value 
                  }))}
                />
                <p className="text-xs text-slate-500">
                  Cette personne recevra les rappels J-1 et Jour J pour tous les acomptes
                </p>
              </div>

              <Separator />

              {/* CC recipients */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  Copies (CC)
                </Label>
                
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Ajouter une adresse en copie..."
                    value={newCcEmail}
                    onChange={(e) => setNewCcEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCcEmail())}
                  />
                  <Button onClick={addCcEmail} variant="outline">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                
                {settings.cc_emails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {settings.cc_emails.map((email) => (
                      <Badge 
                        key={email} 
                        variant="secondary"
                        className="flex items-center gap-1 pr-1"
                      >
                        {email}
                        <button 
                          onClick={() => removeCcEmail(email)}
                          className="ml-1 hover:bg-slate-300 rounded p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                
                {settings.cc_emails.length === 0 && (
                  <p className="text-xs text-slate-400 italic">
                    Aucune adresse en copie
                  </p>
                )}
              </div>

              <Separator />

              {/* Sender name */}
              <div className="space-y-2">
                <Label htmlFor="sender_name">Nom de l'expéditeur</Label>
                <Input
                  id="sender_name"
                  placeholder="BE-TAYE RH"
                  value={settings.sender_name}
                  onChange={(e) => setSettings(prev => ({ 
                    ...prev, 
                    sender_name: e.target.value 
                  }))}
                />
                <p className="text-xs text-slate-500">
                  Apparaîtra comme "{settings.sender_name || 'BE-TAYE RH'} &lt;rh@betaye.fr&gt;"
                </p>
              </div>

              <Separator />

              {/* Enable/disable reminders */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <Label>Activer les rappels automatiques</Label>
                  <p className="text-sm text-slate-500">
                    Envoi automatique des emails J-1 et Jour J
                  </p>
                </div>
                <Switch
                  checked={settings.reminder_enabled}
                  onCheckedChange={(checked) => setSettings(prev => ({ 
                    ...prev, 
                    reminder_enabled: checked 
                  }))}
                />
              </div>

              {/* Heures d'envoi */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-600" />
                  <Label className="font-medium">Heure d&apos;envoi automatique</Label>
                </div>
                <p className="text-sm text-slate-500">
                  Fuseau horaire Europe/Paris. Le job vérifie toutes les 10 minutes ; l&apos;email part dans la fenêtre configurée.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="send-time-j2" className="text-xs text-slate-600">Rappel J-2 (2 jours avant)</Label>
                    <Input
                      id="send-time-j2"
                      type="time"
                      value={settings.send_time_j2}
                      onChange={(e) => setSettings((p) => ({ ...p, send_time_j2: e.target.value }))}
                      disabled={!settings.reminder_enabled}
                      data-testid="send-time-j2-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="send-time-j1" className="text-xs text-slate-600">Rappel J-1 (veille)</Label>
                    <Input
                      id="send-time-j1"
                      type="time"
                      value={settings.send_time_j1}
                      onChange={(e) => setSettings((p) => ({ ...p, send_time_j1: e.target.value }))}
                      disabled={!settings.reminder_enabled}
                      data-testid="send-time-j1-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="send-time-j0" className="text-xs text-slate-600">Rappel Jour J (échéance)</Label>
                    <Input
                      id="send-time-j0"
                      type="time"
                      value={settings.send_time_j0}
                      onChange={(e) => setSettings((p) => ({ ...p, send_time_j0: e.target.value }))}
                      disabled={!settings.reminder_enabled}
                      data-testid="send-time-j0-input"
                    />
                  </div>
                </div>
              </div>

              {/* Summary */}
              <Card className="bg-slate-50">
                <CardContent className="pt-4">
                  <h4 className="text-sm font-medium mb-2">Récapitulatif</h4>
                  <div className="text-sm space-y-1">
                    <p>
                      <span className="text-slate-500">À :</span>{" "}
                      {settings.hr_notification_email || <span className="text-red-500 italic">Non configuré</span>}
                    </p>
                    <p>
                      <span className="text-slate-500">CC :</span>{" "}
                      {settings.cc_emails.length > 0 
                        ? settings.cc_emails.join(", ")
                        : <span className="italic text-slate-400">Aucun</span>
                      }
                    </p>
                    <p>
                      <span className="text-slate-500">Expéditeur :</span>{" "}
                      {settings.sender_name} &lt;rh@betaye.fr&gt;
                    </p>
                    <p>
                      <span className="text-slate-500">Envoi J-2 :</span>{" "}
                      <span className="font-mono">{settings.send_time_j2}</span>
                      {" · "}
                      <span className="text-slate-500">J-1 :</span>{" "}
                      <span className="font-mono">{settings.send_time_j1}</span>
                      {" · "}
                      <span className="text-slate-500">Jour J :</span>{" "}
                      <span className="font-mono">{settings.send_time_j0}</span>
                    </p>
                    <p>
                      <span className="text-slate-500">Statut :</span>{" "}
                      {settings.reminder_enabled 
                        ? <Badge className="bg-emerald-100 text-emerald-700">Actif</Badge>
                        : <Badge className="bg-slate-100 text-slate-500">Désactivé</Badge>
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="flex-1 overflow-y-auto mt-4 pr-2">
            <div className="space-y-4">
              {/* Type selector */}
              <div className="flex gap-2 mb-4 flex-wrap">
                <Button
                  variant={previewType === "j2" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setPreviewType("j2");
                    fetchPreview("j2");
                  }}
                  className="flex items-center gap-2"
                  data-testid="preview-j2-btn"
                >
                  <Clock className="h-4 w-4" />
                  Rappel J-2
                </Button>
                <Button
                  variant={previewType === "j1" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setPreviewType("j1");
                    fetchPreview("j1");
                  }}
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4" />
                  Rappel J-1
                </Button>
                <Button
                  variant={previewType === "j0" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setPreviewType("j0");
                    fetchPreview("j0");
                  }}
                  className="flex items-center gap-2"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Rappel Jour J (urgent)
                </Button>
              </div>

              {loadingPreview ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                </div>
              ) : preview ? (
                <div className="space-y-4">
                  {/* Email header */}
                  <Card>
                    <CardContent className="pt-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-slate-500 w-16">Objet :</span>
                        <span className="text-sm font-medium">{preview.subject}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-slate-500 w-16">De :</span>
                        <span className="text-sm">{settings.sender_name} &lt;rh@betaye.fr&gt;</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="text-sm text-slate-500 w-16">À :</span>
                        <span className="text-sm">{settings.hr_notification_email || "destinataire@exemple.fr"}</span>
                      </div>
                      {settings.cc_emails.length > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="text-sm text-slate-500 w-16">CC :</span>
                          <span className="text-sm">{settings.cc_emails.join(", ")}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Email preview */}
                  <Card>
                    <CardContent className="pt-4">
                      <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                        <Eye className="h-4 w-4" />
                        Aperçu de l'email
                      </h4>
                      <div className="border rounded-lg overflow-hidden" style={{height: '350px'}}>
                        <iframe
                          srcDoc={preview.html}
                          title="Email preview"
                          className="w-full h-full border-0"
                          sandbox="allow-same-origin"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Sample data info */}
                  <div className="text-xs text-slate-500 text-center">
                    Exemple avec : {preview.sample_data?.employee_name} - {preview.sample_data?.amount}€ - {preview.sample_data?.month}
                  </div>
                </div>
              ) : null}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4 pt-4 border-t flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={saveSettings} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
